import { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Users, Download, Shield, ArrowRight, RefreshCw } from "lucide-react";
import api from "@/lib/api";

/**
 * AdminWaitlist — /admin/waitlist. Read-only listing of payment
 * waitlist rows (INR/PKR users right now) so we can act on them the
 * moment we wire Razorpay / JazzCash.
 *
 * Includes a client-side CSV export so the marketing team can drop
 * these into any downstream tool without touching Mongo.
 */
export default function AdminWaitlist() {
  const [secret, setSecret] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    if (!secret) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/waitlist/payments/admin/list", {
        headers: { "X-Admin-Secret": secret },
      });
      setData(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Load failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const downloadCsv = () => {
    if (!data?.items?.length) return;
    const headers = ["email", "currency", "country_code", "preferred_provider", "created_at"];
    const rows = data.items.map(r => headers.map(h => csvSafe(r[h])).join(","));
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payment-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${data.items.length} rows`);
  };

  const byCurrency = groupBy(data?.items || [], (r) => r.currency || "?");

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <div className="text-xs tracking-[0.24em] uppercase text-[var(--text-muted)] mb-2">
          Admin · Payment Waitlist
        </div>
        <h1 className="text-3xl font-heading font-medium text-[var(--text-primary)]">
          Users waiting for local payments
        </h1>
        <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-xl">
          Users who signed up for INR / PKR payment support. When you
          launch Razorpay / JazzCash / Easypaisa these are the emails
          you email first.
        </p>
      </div>

      <div className="card-flat p-5 mb-6 flex flex-wrap items-center gap-3">
        <Shield size={16} className="text-[var(--text-secondary)]" />
        <input
          type="password"
          className="field-input flex-1 min-w-[240px]"
          placeholder="Paste ADMIN_SECRET"
          value={secret}
          onChange={(e) => setSecret(e.target.value)}
          data-testid="admin-waitlist-secret"
        />
        <button
          className="btn-primary"
          onClick={load}
          disabled={loading || !secret}
          data-testid="admin-waitlist-load"
        >
          <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
          {loading ? "Loading…" : "Load waitlist"}
        </button>
        {data && (
          <button
            className="btn-ghost"
            onClick={downloadCsv}
            disabled={!data.items?.length}
            data-testid="admin-waitlist-csv"
          >
            <Download size={15} /> Export CSV
          </button>
        )}
      </div>

      {error && (
        <div className="card-flat p-4 mb-6 border-[var(--accent-danger)] text-sm text-[var(--accent-danger)]" data-testid="admin-waitlist-error">
          {error}
        </div>
      )}

      {data && (
        <>
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <SummaryCard label="Total on waitlist" value={String(data.total)} icon={<Users size={16} />} testid="admin-waitlist-total" />
            {Object.entries(byCurrency).sort((a, b) => b[1].length - a[1].length).slice(0, 2).map(([cur, rows]) => (
              <SummaryCard
                key={cur}
                label={`${cur} waitlist`}
                value={String(rows.length)}
                sub={topProvider(rows)}
                testid={`admin-waitlist-cur-${cur}`}
              />
            ))}
          </div>

          <div className="card-flat p-5" data-testid="admin-waitlist-table">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="label-eyebrow">Rows ({data.count})</div>
              <div className="text-xs text-[var(--text-muted)]">
                Sorted newest first
              </div>
            </div>

            {data.items.length === 0 ? (
              <div className="text-sm text-[var(--text-muted)] py-6">
                Nobody has joined the waitlist yet.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-[0.14em] text-[var(--text-muted)] border-b border-[var(--border-default)]">
                      <th className="py-2 pr-3 font-medium">Email</th>
                      <th className="py-2 pr-3 font-medium">Currency</th>
                      <th className="py-2 pr-3 font-medium">Country</th>
                      <th className="py-2 pr-3 font-medium">Provider</th>
                      <th className="py-2 pr-3 font-medium">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((r, i) => (
                      <tr key={`${r.email}-${i}`} className="border-b border-[var(--border-default)] last:border-0" data-testid={`admin-waitlist-row-${i}`}>
                        <td className="py-2 pr-3 font-mono text-[12px] text-[var(--text-primary)]">{r.email}</td>
                        <td className="py-2 pr-3">{r.currency}</td>
                        <td className="py-2 pr-3 text-[var(--text-muted)]">{r.country_code || "—"}</td>
                        <td className="py-2 pr-3 text-[var(--text-muted)]">{r.preferred_provider || "any"}</td>
                        <td className="py-2 pr-3 text-[11px] text-[var(--text-muted)]">
                          {r.created_at ? new Date(r.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      <div className="mt-8 flex flex-wrap gap-3 text-xs">
        <Link to="/admin/metrics" className="btn-ghost" data-testid="admin-waitlist-back-metrics">
          Back to metrics <ArrowRight size={12} />
        </Link>
        <Link to="/admin/users" className="btn-ghost" data-testid="admin-waitlist-goto-users">
          User lookup <ArrowRight size={12} />
        </Link>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, sub, icon, testid }) {
  return (
    <div className="card-flat p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-[var(--text-secondary)]">
        {icon || <Users size={14} />}
        <div className="label-eyebrow">{label}</div>
      </div>
      <div className="text-2xl font-medium font-mono mt-2">{value}</div>
      {sub && <div className="text-[11px] text-[var(--text-muted)] mt-1">{sub}</div>}
    </div>
  );
}

function topProvider(rows) {
  const counts = groupBy(rows, (r) => r.preferred_provider || "any");
  const top = Object.entries(counts).sort((a, b) => b[1].length - a[1].length)[0];
  if (!top) return null;
  return `Top provider: ${top[0]} (${top[1].length})`;
}

function groupBy(arr, fn) {
  const out = {};
  for (const item of arr) {
    const k = fn(item);
    if (!out[k]) out[k] = [];
    out[k].push(item);
  }
  return out;
}

function csvSafe(v) {
  if (v == null) return "";
  const s = String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}
